/**
 * The owner's money reports (Phase 8A, ADR-043). The server computes every figure (public.report_money); this module
 * chooses the period, labels and orders what the server returns, and describes each table once, so that the screen and
 * its CSV file are written from the same rows and the same columns (D6).
 */
import { fractionDigitsFor, minorToInput } from './catalog';
import type { MethodFigures } from './review';
import type { WriteOffKind } from './writeOffs';

/* ── what the server returns ── */

/** One business day of the period (every date of the period is one, with zeros where nothing happened). */
export interface ReportDay {
  date: string;
  /** The goods of the day's orders as they stand: not cancelled or voided, paid or not, less returns. */
  sales_minor: number;
  /** Orders with goods (a total above zero), not cancelled or voided. */
  orders: number;
  /** What the day's orders still owe (unpaid, part paid, or reopened by a refund), as the POS shows it. */
  owed_minor: number;
  written_off_on_orders_minor: number;
  /** The Day view's methods over the day's shifts: received, voided, given back, and the net of the three. */
  captured_minor: number;
  voided_minor: number;
  refunded_minor: number;
  in_shift_net_minor: number;
  /** Given back by the owner outside any shift on the day (the Day view lists it apart). */
  outside_refunded_minor: number;
  /** Money received less money given back: in_shift_net_minor - outside_refunded_minor. */
  takings_minor: number;
  refunds: number;
  payment_voids: number;
  /** Written off on the day, less what was reinstated on the day. */
  written_off_minor: number;
  voided_orders: number;
  voided_orders_minor: number;
}

export type ReportTotals = Omit<ReportDay, 'date'> & { days: number };

export type ReportMethod = MethodFigures;

export interface ReportRefund {
  method: string;
  kind: string;
  outside_shift: boolean;
  count: number;
  amount_minor: number;
}

export interface ReportPaymentVoid {
  method: string;
  kind: string;
  count: number;
  amount_minor: number;
}

export interface ReportHour {
  hour: number;
  sales_minor: number;
  orders: number;
}

export interface ReportWeekday {
  isodow: number;
  days: number;
  sales_minor: number;
  orders: number;
}

export interface ReportWriteOff {
  kind: WriteOffKind;
  count: number;
  written_off_minor: number;
  reinstated: number;
  reinstated_minor: number;
  standing_minor: number;
}

export interface ReportMoney {
  ok: true;
  location_id: string;
  from: string;
  to: string;
  today: string;
  timezone: string;
  /** The business day's start at the location, "HH:MM" (04:00 unless the owner changed it). */
  day_start: string;
  days: ReportDay[];
  methods: ReportMethod[];
  refunds: ReportRefund[];
  payment_voids: ReportPaymentVoid[];
  by_hour: ReportHour[];
  by_weekday: ReportWeekday[];
  write_offs: ReportWriteOff[];
  totals: ReportTotals;
}

/* ── the period ── */

export const REPORT_PRESETS = ['today', 'yesterday', 'week', 'month', 'custom'] as const;
export type ReportPreset = (typeof REPORT_PRESETS)[number];

export const REPORT_PRESET_LABEL: Record<ReportPreset, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  week: 'This week',
  month: 'This month',
  custom: 'Custom',
};

export interface ReportPeriod {
  from: string;
  to: string;
}

/** At most 13 months at a time (the server refuses more): from and to may be 396 days apart. */
export const REPORT_MAX_DAYS = 397;

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** A business date moved by whole days (dates only, no time zone involved). */
export function shiftDate(date: string, days: number): string {
  const d = new Date(`${date}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Whole days from one business date to another (0 when equal). */
export function daysBetween(from: string, to: string): number {
  return Math.round(
    (new Date(`${to}T12:00:00Z`).getTime() - new Date(`${from}T12:00:00Z`).getTime()) / 86_400_000,
  );
}

export function periodLength(p: ReportPeriod): number {
  return daysBetween(p.from, p.to) + 1;
}

function lastDayOfMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** A preset's business days, from the location's business date today. Weeks start on Monday. */
export function presetPeriod(preset: Exclude<ReportPreset, 'custom'>, today: string): ReportPeriod {
  switch (preset) {
    case 'today':
      return { from: today, to: today };
    case 'yesterday': {
      const y = shiftDate(today, -1);
      return { from: y, to: y };
    }
    case 'week': {
      const dow = new Date(`${today}T12:00:00Z`).getUTCDay(); // 0 is Sunday
      return { from: shiftDate(today, -((dow + 6) % 7)), to: today };
    }
    case 'month':
      return { from: `${today.slice(0, 8)}01`, to: today };
  }
}

/**
 * The period a preset is compared with, and how to say it: today with yesterday, yesterday with the day before, this
 * week and this month with the same days of the last week and month, a custom period with as many days just before it.
 */
export function comparisonPeriod(
  preset: ReportPreset,
  period: ReportPeriod,
): ReportPeriod & { label: string; when: string } {
  switch (preset) {
    case 'today':
      return { ...shiftPeriod(period, -1), label: 'yesterday', when: 'yesterday' };
    case 'yesterday':
      return { ...shiftPeriod(period, -1), label: 'the day before', when: 'the day before' };
    case 'week':
      return {
        ...shiftPeriod(period, -7),
        label: 'the same days last week',
        when: 'on the same days last week',
      };
    case 'month': {
      const [y, m] = period.from.split('-').map(Number) as [number, number];
      const py = m === 1 ? y - 1 : y;
      const pm = m === 1 ? 12 : m - 1;
      const day = Math.min(Number(period.to.slice(8, 10)), lastDayOfMonth(py, pm));
      const mm = String(pm).padStart(2, '0');
      return {
        from: `${py}-${mm}-01`,
        to: `${py}-${mm}-${String(day).padStart(2, '0')}`,
        label: 'the same days last month',
        when: 'on the same days last month',
      };
    }
    case 'custom': {
      const n = periodLength(period);
      const label = n === 1 ? 'the day before' : `the ${n} days before`;
      return {
        from: shiftDate(period.from, -n),
        to: shiftDate(period.from, -1),
        label,
        when: n === 1 ? label : `in ${label}`,
      };
    }
  }
}

function shiftPeriod(p: ReportPeriod, days: number): ReportPeriod {
  return { from: shiftDate(p.from, days), to: shiftDate(p.to, days) };
}

/** A real calendar date written YYYY-MM-DD (not 2026-02-30, not 2025-13-01). */
export function isBusinessDate(d: string): boolean {
  if (!ISO_DATE.test(d)) return false;
  const t = new Date(`${d}T12:00:00Z`);
  return !Number.isNaN(t.getTime()) && t.toISOString().slice(0, 10) === d;
}

/** A custom period's error in words, or null when the server will accept it. */
export function customPeriodError(from: string, to: string, today: string): string | null {
  if (!isBusinessDate(from) || !isBusinessDate(to)) return 'Choose both dates.';
  if (from > to) return 'The first date is after the last.';
  if (to > today) return 'The last date has not started yet.';
  if (periodLength({ from, to }) > REPORT_MAX_DAYS) return 'At most 13 months at a time.';
  return null;
}

/* ── figures derived for the screen (never a new definition: only arithmetic on the server's) ── */

/** Sales per order, rounded to the centavo; null when there were no orders (never NaN). */
export function averageOrder(salesMinor: number, orders: number): number | null {
  return orders > 0 ? Math.round(salesMinor / orders) : null;
}

/** The change from a previous figure in whole percent; null when there is nothing to compare with. */
export function percentChange(current: number, previous: number | null | undefined): number | null {
  if (previous == null || previous <= 0) return null;
  return Math.round(((current - previous) / previous) * 100);
}

/** "▲ 12%", "▼ 3%", "About the same" (under half a percent): a word and a shape, never colour alone. */
export function changeText(pct: number): string {
  if (pct === 0) return 'About the same';
  return `${pct > 0 ? '▲' : '▼'} ${Math.abs(pct)}%`;
}

/** The business day's first hour from "HH:MM" (4 for 04:00). */
export function dayStartHour(dayStart: string | null | undefined): number {
  const m = /^(\d{1,2}):\d{2}/.exec(dayStart ?? '');
  const h = m ? Number(m[1]) : NaN;
  return h >= 0 && h < 24 ? h : 4;
}

/**
 * The hours with sales, in the order of the business day (from its start), from the first hour that sold to the last
 * one, with the quiet hours between them as zeros. Empty when nothing sold.
 */
export function hoursOfDay(
  byHour: ReportHour[],
  dayStart: string | null | undefined,
): ReportHour[] {
  const start = dayStartHour(dayStart);
  const m = new Map(byHour.map((h) => [h.hour, h]));
  const all = Array.from({ length: 24 }, (_, i) => {
    const hour = (start + i) % 24;
    return m.get(hour) ?? { hour, sales_minor: 0, orders: 0 };
  });
  const first = all.findIndex((h) => h.orders > 0 || h.sales_minor !== 0);
  if (first < 0) return [];
  let last = all.length - 1;
  while (last > first && all[last]!.orders === 0 && all[last]!.sales_minor === 0) last--;
  return all.slice(first, last + 1);
}

/** "07:00–08:00". */
export function hourLabel(hour: number): string {
  const h = (n: number) => `${String(n % 24).padStart(2, '0')}:00`;
  return `${h(hour)}–${h(hour + 1)}`;
}

export const WEEKDAY_LABEL = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

export interface WeekdayRow {
  label: string;
  days: number;
  sales_minor: number;
  orders: number;
  /** Sales per business day of that weekday in the period; null when the period has none of it. */
  average_minor: number | null;
}

/** Each weekday of the period, Monday first. */
export function weekdays(byWeekday: ReportWeekday[]): WeekdayRow[] {
  const m = new Map(byWeekday.map((w) => [w.isodow, w]));
  return WEEKDAY_LABEL.map((label, i) => {
    const w = m.get(i + 1);
    const days = w?.days ?? 0;
    const sales = w?.sales_minor ?? 0;
    return {
      label,
      days,
      sales_minor: sales,
      orders: w?.orders ?? 0,
      average_minor: days > 0 ? Math.round(sales / days) : null,
    };
  });
}

export const REPORT_WRITE_OFF_LABEL: Record<WriteOffKind, string> = {
  walkout: 'Walk-outs',
  staff_meal: 'Staff meals',
  comp: 'Comps',
};

/** True when the period recorded nothing at all: no sale, no money, nothing given back, voided or written off. */
export function isQuietPeriod(t: ReportTotals, writeOffs: ReportWriteOff[] = []): boolean {
  return (
    writeOffs.length === 0 &&
    t.refunds === 0 &&
    t.payment_voids === 0 &&
    t.sales_minor === 0 &&
    t.orders === 0 &&
    t.captured_minor === 0 &&
    t.voided_minor === 0 &&
    t.refunded_minor === 0 &&
    t.outside_refunded_minor === 0 &&
    t.written_off_minor === 0 &&
    t.voided_orders === 0 &&
    t.owed_minor === 0
  );
}

/* ── tables: one description for the screen and the CSV ── */

export type ReportCellKind = 'text' | 'date' | 'money' | 'count';
export type ReportCell = string | number | null;

export interface ReportColumn<T> {
  header: string;
  kind: ReportCellKind;
  value: (row: T) => ReportCell;
}

export interface ReportTable<T> {
  /** The CSV file's name, without the period or extension ("sales-by-day"). */
  name: string;
  columns: ReportColumn<T>[];
  rows: T[];
  /** The totals row, shown last on the screen and written last in the file. */
  total?: T;
}

/* ── CSV ── */

/** Money as a plain decimal amount ("1234.50", "-12.00"): no currency sign, no grouping, for spreadsheets. */
export function csvMoney(minor: number, currency = 'PHP'): string {
  return minorToInput(minor, fractionDigitsFor(currency));
}

const NUMERIC = /^-?\d+(\.\d+)?$/;

function csvCell(v: string): string {
  // a text that a spreadsheet would run as a formula is written as text (names come from the owner's settings)
  const s = /^[=+\-@\t\r]/.test(v) && !NUMERIC.test(v) ? `'${v}` : v;
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** A cell as the file writes it: money as a decimal, counts as integers, dates as YYYY-MM-DD, nothing as empty. */
export function csvValue(kind: ReportCellKind, v: ReportCell, currency = 'PHP'): string {
  if (v == null) return '';
  if (kind === 'money') return csvMoney(Number(v), currency);
  return String(v);
}

/** RFC 4180: a header row, one row per item, the totals row last; CRLF line ends. */
export function tableToCsv<T>(table: ReportTable<T>, currency = 'PHP'): string {
  const lines = [table.columns.map((c) => csvCell(c.header)).join(',')];
  const rows = table.total ? [...table.rows, table.total] : table.rows;
  for (const r of rows)
    lines.push(table.columns.map((c) => csvCell(csvValue(c.kind, c.value(r), currency))).join(','));
  return `${lines.join('\r\n')}\r\n`;
}

/** "sales-by-day_2026-09-01_2026-09-30.csv". */
export function csvFileName(name: string, p: ReportPeriod): string {
  return p.from === p.to ? `${name}_${p.from}.csv` : `${name}_${p.from}_${p.to}.csv`;
}

/* ── the report's tables ── */

const TOTAL = 'Total';

type DayRow = Pick<
  ReportDay,
  | 'date'
  | 'sales_minor'
  | 'orders'
  | 'takings_minor'
  | 'refunds'
  | 'refunded_minor'
  | 'outside_refunded_minor'
  | 'payment_voids'
  | 'voided_minor'
>;

function totalDay(t: ReportTotals): DayRow {
  return { ...t, date: TOTAL };
}

/** Sales by day: every date of the period. */
export function salesByDayTable(r: ReportMoney): ReportTable<DayRow> {
  return {
    name: 'sales-by-day',
    columns: [
      { header: 'Date', kind: 'date', value: (d) => d.date },
      { header: 'Sales', kind: 'money', value: (d) => d.sales_minor },
      { header: 'Orders', kind: 'count', value: (d) => d.orders },
      {
        header: 'Average order',
        kind: 'money',
        value: (d) => averageOrder(d.sales_minor, d.orders),
      },
    ],
    rows: r.days,
    total: totalDay(r.totals),
  };
}

/** Sales by hour of the business day, from the first hour that sold to the last. */
export function salesByHourTable(r: ReportMoney): ReportTable<ReportHour & { label: string }> {
  return {
    name: 'sales-by-hour',
    columns: [
      { header: 'Hour', kind: 'text', value: (h) => h.label },
      { header: 'Sales', kind: 'money', value: (h) => h.sales_minor },
      { header: 'Orders', kind: 'count', value: (h) => h.orders },
    ],
    rows: hoursOfDay(r.by_hour, r.day_start).map((h) => ({ ...h, label: hourLabel(h.hour) })),
    total: { hour: -1, label: TOTAL, sales_minor: r.totals.sales_minor, orders: r.totals.orders },
  };
}

/** Sales by weekday, Monday first, with the sales of an average day of each. */
export function salesByWeekdayTable(r: ReportMoney): ReportTable<WeekdayRow> {
  return {
    name: 'sales-by-weekday',
    columns: [
      { header: 'Weekday', kind: 'text', value: (w) => w.label },
      { header: 'Days', kind: 'count', value: (w) => w.days },
      { header: 'Sales', kind: 'money', value: (w) => w.sales_minor },
      { header: 'Orders', kind: 'count', value: (w) => w.orders },
      { header: 'Average day', kind: 'money', value: (w) => w.average_minor },
    ],
    rows: weekdays(r.by_weekday),
    total: {
      label: TOTAL,
      days: r.totals.days,
      sales_minor: r.totals.sales_minor,
      orders: r.totals.orders,
      average_minor: r.totals.days > 0 ? Math.round(r.totals.sales_minor / r.totals.days) : null,
    },
  };
}

/** Sales and Takings side by side, by day: two figures, never one. */
export function salesVsTakingsTable(r: ReportMoney): ReportTable<DayRow> {
  return {
    name: 'sales-vs-takings',
    columns: [
      { header: 'Date', kind: 'date', value: (d) => d.date },
      { header: 'Sales', kind: 'money', value: (d) => d.sales_minor },
      { header: 'Takings', kind: 'money', value: (d) => d.takings_minor },
      {
        header: 'Takings less sales',
        kind: 'money',
        value: (d) => d.takings_minor - d.sales_minor,
      },
    ],
    rows: r.days,
    total: totalDay(r.totals),
  };
}

export interface MethodRow {
  method: string;
  where: string;
  count: number | null;
  captured_minor: number | null;
  voided_minor: number | null;
  refunded_minor: number;
  net_minor: number;
}

/**
 * The payment methods as the Day view lists them (received, voided, given back, net, over the period's shifts), then
 * what the owner gave back outside any shift by method; the net column adds up to Takings.
 */
export function methodsTable(r: ReportMoney): ReportTable<MethodRow> {
  const inShift: MethodRow[] = r.methods.map((m) => ({
    method: m.method,
    where: 'In shifts',
    count: m.count,
    captured_minor: m.captured_minor,
    voided_minor: m.voided_minor,
    refunded_minor: m.refunded_minor,
    net_minor: m.net_minor,
  }));
  const outside: MethodRow[] = r.refunds
    .filter((g) => g.outside_shift)
    .map((g) => ({
      method: g.method,
      where: 'Outside a shift',
      count: null,
      captured_minor: null,
      voided_minor: null,
      refunded_minor: g.amount_minor,
      net_minor: -g.amount_minor,
    }));
  return {
    name: 'payment-methods',
    columns: [
      { header: 'Method', kind: 'text', value: (m) => m.method },
      { header: 'Where', kind: 'text', value: (m) => m.where },
      { header: 'Payments', kind: 'count', value: (m) => m.count },
      { header: 'Received', kind: 'money', value: (m) => m.captured_minor },
      { header: 'Voided', kind: 'money', value: (m) => m.voided_minor },
      { header: 'Refunded', kind: 'money', value: (m) => m.refunded_minor },
      { header: 'Net', kind: 'money', value: (m) => m.net_minor },
    ],
    rows: [...inShift, ...outside],
    total: {
      method: TOTAL,
      where: '',
      count: r.methods.reduce((n, m) => n + m.count, 0),
      captured_minor: r.totals.captured_minor,
      voided_minor: r.totals.voided_minor,
      refunded_minor: r.totals.refunded_minor + r.totals.outside_refunded_minor,
      net_minor: r.totals.takings_minor,
    },
  };
}

/** Refunds by method: in a shift and outside any shift. */
export function refundsTable(r: ReportMoney): ReportTable<ReportRefund & { where: string }> {
  return {
    name: 'refunds',
    columns: [
      { header: 'Method', kind: 'text', value: (g) => g.method },
      { header: 'Where', kind: 'text', value: (g) => g.where },
      { header: 'Refunds', kind: 'count', value: (g) => g.count },
      { header: 'Amount', kind: 'money', value: (g) => g.amount_minor },
    ],
    rows: r.refunds.map((g) => ({
      ...g,
      where: g.outside_shift ? 'Outside a shift' : 'In a shift',
    })),
    total: {
      method: TOTAL,
      kind: '',
      outside_shift: false,
      where: '',
      count: r.totals.refunds,
      amount_minor: r.totals.refunded_minor + r.totals.outside_refunded_minor,
    },
  };
}

/** The days that gave money back. */
export function refundsByDayTable(r: ReportMoney): ReportTable<DayRow> {
  return {
    name: 'refunds-by-day',
    columns: [
      { header: 'Date', kind: 'date', value: (d) => d.date },
      { header: 'Refunds', kind: 'count', value: (d) => d.refunds },
      { header: 'In shifts', kind: 'money', value: (d) => d.refunded_minor },
      { header: 'Outside a shift', kind: 'money', value: (d) => d.outside_refunded_minor },
      {
        header: 'Amount',
        kind: 'money',
        value: (d) => d.refunded_minor + d.outside_refunded_minor,
      },
    ],
    rows: r.days.filter((d) => d.refunds > 0 || d.refunded_minor + d.outside_refunded_minor !== 0),
    total: totalDay(r.totals),
  };
}

/** Payments voided, by method. */
export function paymentVoidsTable(r: ReportMoney): ReportTable<ReportPaymentVoid> {
  return {
    name: 'payment-voids',
    columns: [
      { header: 'Method', kind: 'text', value: (v) => v.method },
      { header: 'Voids', kind: 'count', value: (v) => v.count },
      { header: 'Amount', kind: 'money', value: (v) => v.amount_minor },
    ],
    rows: r.payment_voids,
    total: {
      method: TOTAL,
      kind: '',
      count: r.totals.payment_voids,
      amount_minor: r.totals.voided_minor,
    },
  };
}

/** The days that voided a payment. */
export function paymentVoidsByDayTable(r: ReportMoney): ReportTable<DayRow> {
  return {
    name: 'payment-voids-by-day',
    columns: [
      { header: 'Date', kind: 'date', value: (d) => d.date },
      { header: 'Voids', kind: 'count', value: (d) => d.payment_voids },
      { header: 'Amount', kind: 'money', value: (d) => d.voided_minor },
    ],
    rows: r.days.filter((d) => d.payment_voids > 0 || d.voided_minor !== 0),
    total: totalDay(r.totals),
  };
}

type WriteOffRow = Omit<ReportWriteOff, 'kind'> & { label: string };

/** Write-offs by kind, on the day each was recorded; reinstatements on theirs. */
export function writeOffsTable(r: ReportMoney): ReportTable<WriteOffRow> {
  const sum = (k: keyof Omit<ReportWriteOff, 'kind'>) => r.write_offs.reduce((n, w) => n + w[k], 0);
  return {
    name: 'write-offs',
    columns: [
      { header: 'Kind', kind: 'text', value: (w) => w.label },
      { header: 'Written off', kind: 'count', value: (w) => w.count },
      { header: 'Amount', kind: 'money', value: (w) => w.written_off_minor },
      { header: 'Reinstated', kind: 'count', value: (w) => w.reinstated },
      { header: 'Reinstated amount', kind: 'money', value: (w) => w.reinstated_minor },
      { header: 'Net written off', kind: 'money', value: (w) => w.standing_minor },
    ],
    rows: r.write_offs.map((w) => ({ ...w, label: REPORT_WRITE_OFF_LABEL[w.kind] ?? w.kind })),
    total: {
      label: TOTAL,
      count: sum('count'),
      written_off_minor: sum('written_off_minor'),
      reinstated: sum('reinstated'),
      reinstated_minor: sum('reinstated_minor'),
      standing_minor: r.totals.written_off_minor,
    },
  };
}
